import { useEffect, useRef } from 'react';

import {
  NotificationManager,
  SessionStorageManager,
  SiteConfigManager,
  useApplicationState,
} from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes } from '@atlas/routing';

import history from 'components/Layout/history';
import { DEPOSIT_FAILED } from 'src/common/constants';
import { showAlternativeMethodsOrError } from 'src/common/Notifications/Popups/alternativePaymentUtils';

const { CLIENT_PROFILE } = COMPONENT_TYPES;
const { payment } = clientProfileRoutes;
const { deposit, transactionHistory } = paymentRoutes;

const parseDepositFailedData = value => {
  try {
    const parsedValue = JSON.parse(value);

    return {
      alternativePmEnabled: !!parsedValue?.alternativePmEnabled,
      pmId: parsedValue?.pmId,
    };
  } catch {
    return {
      alternativePmEnabled: !!value,
      pmId: value,
    };
  }
};

const useDepositFailedNotification = () => {
  const { authenticated, resolved } = useApplicationState();
  const depositFailedRef = useRef(SessionStorageManager.getItem(DEPOSIT_FAILED));

  if (depositFailedRef.current !== null) {
    SessionStorageManager.removeItem(DEPOSIT_FAILED);
  }

  useEffect(() => {
    const isNotReady = !resolved || !authenticated || depositFailedRef.current === null;
    if (isNotReady) return;

    if (window.location.pathname.includes(transactionHistory)) {
      depositFailedRef.current = null;
      return;
    }

    const { alternativePmEnabled, pmId } = parseDepositFailedData(depositFailedRef.current);
    depositFailedRef.current = null;

    const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);
    const depositPageLink = `${clientProfilePath}/${payment}/${deposit}`;

    const fallbackError = () => {
      NotificationManager.display({
        data: {
          headerTitle: 'deposit',
          text: 'deposit_failed_message',
          title: 'deposit_failed_title',
          withHeader: false,
        },
        type: 'error',
      });
    };

    if (!alternativePmEnabled) {
      fallbackError();
      return;
    }

    showAlternativeMethodsOrError({
      fallbackError,
      onSelectMethod: method => history.push(`${depositPageLink}/${method.id}`),
      onViewAll: () => history.push(depositPageLink),
      operationType: 'DEPOSIT',
      pmId: Number(pmId),
    });
  }, [resolved, authenticated]);
};

export default useDepositFailedNotification;
